import { useMount, useUnmount } from "ahooks";
import { isEmpty, last, uniqueId } from "lodash";
import React, { useState } from "react";
import { UnassignEventGuidePrompt } from "@/components/Dashboard/Resources/Guides/UnassignEventGuides";
import { useUnassignGuidePromptStore } from "@/stores/unassignGuidePromptStore";

interface UnassignGuidePromptHostProps {
    /**
     * Mark the app-level host, which only renders when no roster is mounted. A roster-level host wins because
     * ui-kit nests its prompt inside the roster drawer's dialog; an app-level one would be that drawer's sibling,
     * and sibling dialogs close each other on outside clicks and fight over focus.
     */
    isFallback?: boolean;
}

export const UnassignGuidePromptHost = ({ isFallback = false }: UnassignGuidePromptHostProps) => {
    const event = useUnassignGuidePromptStore((state) => state.event);
    const close = useUnassignGuidePromptStore((state) => state.close);
    const hostIds = useUnassignGuidePromptStore((state) => state.hostIds);
    const claimHost = useUnassignGuidePromptStore((state) => state.claimHost);
    const releaseHost = useUnassignGuidePromptStore((state) => state.releaseHost);
    const [hostId] = useState(() => uniqueId("unassign-guide-prompt-host-"));

    useMount(() => {
        if (!isFallback) {
            claimHost(hostId);
        }
    });

    useUnmount(() => {
        if (!isFallback) {
            releaseHost(hostId);
        }
    });

    const isActiveHost = isFallback ? isEmpty(hostIds) : last(hostIds) === hostId;

    if (!event || !isActiveHost) {
        return null;
    }

    return <UnassignEventGuidePrompt isOpen events={event} onClose={close} />;
};
